// src/features/settings/components/stock-locations-panel/StockLocationsGrid.jsx
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

export default function StockLocationsGrid({
  stockLocations,
  loading,
  canUpdate,
  canDelete,
  onToggleActive,
  onDelete,
  processRowUpdate,
  onProcessRowUpdateError,
}) {
  const { t } = useTranslation(['settings', 'common']);

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('settings:stock_locations.grid.name'),
      flex: 1,
      minWidth: 150,
      editable: canUpdate,
    },
    {
      field: 'description',
      headerName: t('settings:stock_locations.grid.description'),
      flex: 2,
      minWidth: 200,
      editable: canUpdate,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'active',
      headerName: t('settings:stock_locations.grid.active'),
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.active ? t('common:status.active') : t('common:status.inactive')}
          color={params.row.active ? 'success' : 'default'}
          size="small"
          onClick={canUpdate && !loading ? () => onToggleActive(params.row) : undefined}
          sx={canUpdate ? { cursor: 'pointer' } : undefined}
        />
      ),
    },
    {
      field: 'actions',
      renderHeader: () => (<></>),
      width: 52,
      sortable: false,
      filterable: false,
      renderCell: (params) => canDelete ? (
        <Box>
          <IconButton
            size="small"
            onClick={() => onDelete(params.row)}
            title={t('common:buttons.delete')}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null,
    },
  ], [t, canUpdate, canDelete, loading, onToggleActive, onDelete]);

  return (
    <DataGrid
      rows={stockLocations}
      columns={columns}
      getRowId={(row) => row.stock_location_id}
      getRowClassName={(params) => !params.row.active ? 'row-inactive' : ''}
      processRowUpdate={processRowUpdate}
      onProcessRowUpdateError={onProcessRowUpdateError}
      pageSizeOptions={[10, 25, 50]}
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      disableColumnFilter
      disableColumnSorting
      disableColumnMenu
      disableColumnResize
      disableRowSelectionOnClick
      sx={{
        '& .row-inactive': {
          opacity: 0.5,
        },
        minHeight: 400,
      }}
    />
  );
}
